<script setup lang="ts">
import { useExplorerStore } from "@/modules/explorer/store";
import { onBeforeMount, ref } from "vue";
import ExplorerIndex from "@/modules/explorer/pages/ExplorerIndex.vue";

import { useDocumentHistory } from "@/modules/explorer/documentHistoryStore";
import { useApiClient } from "@/api/client";
import { useRoute, useRouter } from "vue-router";
import { useSegmentStore } from "@/modules/explorer/SegmentStore";
import { toActivePinFromFavoriteDimension } from "@/modules/explorer/explorerTypes";
import { convertDates } from "chartjs-graph";
import { promptInvalidPermissions } from "@/api/helpers/loadingWrapper";
import useToast from "@/ui/composables/useToast";
import { v4 as uuidv4 } from "uuid";
import DocumentDO = App.Data.Explorer.DocumentDO;

/**
 * Opens a shared configuration. The backend hands back a replica under a fresh
 * uuid, so exploring from here never touches the sharer's own history.
 */
const openSharedDocument = async (uuid: string) => {
	const response = await promptInvalidPermissions(
		useApiClient("explorer").getShareLinkDocument(uuid)
	);

	if (response.isErr()) {
		useToast().error("This share link is no longer available");
		await useExplorerStore().FETCH_HOME_DOCUMENT(null);
		return;
	}

	const explorerStore = useExplorerStore();

	explorerStore.document = { ...convertDates(response.value) };
	explorerStore.explorerState.userAbilityForCurrentDocument = ["view"];
	explorerStore.explorerState.lastOpenedSavedDocumentDocumentUuid = null;

	useDocumentHistory().addDocumentToHistory(explorerStore.document);

	explorerStore.explorerState.isDocumentLoaded = true;
};

/**
 * Opens a dashboard card as the dashboard shows it. It's detached from its
 * stored document so changes can't overwrite the card's configuration by
 * accident; it can still be saved over explicitly via the save dialog.
 */
const openDashboardCardDocument = (document: DocumentDO) => {
	const explorerStore = useExplorerStore();

	explorerStore.document = {
		...convertDates(document),
		parent_uuid: document.uuid,
		uuid: uuidv4(),
	};
	explorerStore.explorerState.userAbilityForCurrentDocument = ["view"];
	explorerStore.explorerState.lastOpenedSavedDocumentDocumentUuid = null;

	useDocumentHistory().addDocumentToHistory(explorerStore.document);

	explorerStore.explorerState.isDocumentLoaded = true;
};

const isLoading = ref(true);

onBeforeMount(async () => {
	const route = useRoute();
	const router = useRouter();

	const baseDocumentUuid = Array.isArray(route.query["base-document"])
		? route.query["base-document"][0]
		: route.query["base-document"];

	const dashboardUuid = Array.isArray(route.query["dashboard"])
		? route.query["dashboard"][0]
		: route.query["dashboard"];

	const sharedDocumentUuid = Array.isArray(route.query["shared-document"])
		? route.query["shared-document"][0]
		: route.query["shared-document"];

	// "fresh" requests a blank document instead of resuming the most recent
	// one, e.g. when creating a new document for a dashboard card.
	const startFresh = !!route.query["fresh"];

	const redirectToAlertsAfterSave = !!route.query["redirect-to-alerts"];

	useExplorerStore().explorerState.dashboardRedirectUuid =
		dashboardUuid ?? null;
	useExplorerStore().explorerState.redirectToAlertsAfterSave =
		redirectToAlertsAfterSave;

	const dashboardCardDocument =
		useExplorerStore().explorerState.dashboardCardDocumentToView;
	useExplorerStore().explorerState.dashboardCardDocumentToView = null;

	if (dashboardCardDocument) {
		openDashboardCardDocument(dashboardCardDocument);
	} else if (sharedDocumentUuid) {
		await openSharedDocument(sharedDocumentUuid);
	} else if (startFresh) {
		await useExplorerStore().FETCH_HOME_DOCUMENT(null);
	} else {
		const response = await useApiClient("explorer").getDocumentHistory(
			baseDocumentUuid
		);
		if (response.isOk()) {
			if (response.value.length > 0) {
				useDocumentHistory().documentHistory = response.value;
				useDocumentHistory().update();
				useDocumentHistory().getMostRecentDocument();

				if (baseDocumentUuid) {
					if (response.value.length > 0) {
						const visibleDocument = response.value[0];
						if (
							visibleDocument?.active_user_abilities &&
							(visibleDocument.active_user_abilities.includes("update") ||
								visibleDocument.active_user_abilities.includes("*"))
						) {
							useExplorerStore().explorerState.userAbilityForCurrentDocument =
								visibleDocument.active_user_abilities;

							useExplorerStore().explorerState.lastOpenedSavedDocumentDocumentUuid =
								useExplorerStore().document.uuid;
						}
					}
				}

				useExplorerStore().explorerState.isDocumentLoaded = true;
			} else {
				await useExplorerStore().FETCH_HOME_DOCUMENT(null);
			}
		} else {
			await useExplorerStore().FETCH_HOME_DOCUMENT(null);
		}
	}

	if (redirectToAlertsAfterSave) {
		useExplorerStore().document.view_tab = "table";
	}

	if (
		baseDocumentUuid ||
		dashboardUuid ||
		sharedDocumentUuid ||
		startFresh ||
		redirectToAlertsAfterSave
	) {
		router.replace({ query: null });
	}

	await useSegmentStore().load();

	const userFavorites = await useApiClient(
		"users"
	).getCurrentUserFavoriteDimensions();
	if (userFavorites.isOk()) {
		userFavorites.value.forEach((favorite) => {
			useExplorerStore().ADD_PIN(
				toActivePinFromFavoriteDimension(favorite),
				false
			);
		});
	}

	isLoading.value = false;
});
</script>

<template>
	<explorer-index
		v-if="useExplorerStore().explorerState.isDocumentLoaded && !isLoading"
	/>
	<div
		style="
			display: flex;
			justify-content: center;
			align-items: center;
			height: 100vh;
		"
		v-else
	>
		<div>
			<div style="width: 400px">
				<div>
					<v-progress-linear
						class="mb-5"
						close-delay="0"
						color="primary"
						height="6"
						indeterminate
						open-delay="0"
						rounded
					/>
				</div>
				<div style="text-align: center">Setting up Explorer module ..</div>
			</div>
		</div>
	</div>
</template>
